import React, { useState } from "react";
import apiRequest from "../../../../utils/apiClient";
import { autoPostUrl } from "../../../constants";
import { AUTO_POST_TEXT } from "../constants/myLibraryText";
import { CreationData } from "../types";

interface AutoPostButtonProps {
  creationId: number;
  state?: CreationData["auto_state"];
  /** The row's new state once the server answers (sent / failed). */
  onDone: (creationId: number, state: "sent" | "failed") => void;
  onMessage: (text: string, tone: "success" | "error") => void;
}

/**
 * 📤 Post — sends one finished video to the account's Auto webhook now. The row shows it
 * when Auto is set up: a video held for review ("waiting", highlighted), one exported with
 * Auto off, or a retry. Once sent it reads "✓ Sent" and can be pressed again.
 */
const AutoPostButton = ({ creationId, state = "", onDone, onMessage }: AutoPostButtonProps) => {
  const [busy, setBusy] = useState(false);

  const press = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const response = await apiRequest(autoPostUrl(creationId), { method: "POST" });
      let data: { message?: string } | null = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }
      if (response.ok) {
        onDone(creationId, "sent");
        onMessage(AUTO_POST_TEXT.SENT_MESSAGE, "success");
      } else {
        if (response.status === 502) onDone(creationId, "failed");
        onMessage(data?.message || AUTO_POST_TEXT.FAILED_MESSAGE, "error");
      }
    } catch {
      onMessage(AUTO_POST_TEXT.FAILED_MESSAGE, "error");
    } finally {
      setBusy(false);
    }
  };

  const label = busy
    ? AUTO_POST_TEXT.POSTING
    : state === "sent"
      ? AUTO_POST_TEXT.SENT
      : state === "failed"
        ? AUTO_POST_TEXT.RETRY
        : AUTO_POST_TEXT.POST;
  const title =
    state === "waiting"
      ? AUTO_POST_TEXT.WAITING_TITLE
      : state === "sent"
        ? AUTO_POST_TEXT.SENT_TITLE
        : state === "failed"
          ? AUTO_POST_TEXT.FAILED_TITLE
          : AUTO_POST_TEXT.POST_TITLE;

  return (
    <button
      type="button"
      className={`cc-export-action cc-auto-post${state === "waiting" ? " is-waiting" : ""}${state === "sent" ? " is-sent" : ""}`}
      title={title}
      aria-label={title}
      disabled={busy}
      onClick={press}
    >
      {label}
    </button>
  );
};

export default AutoPostButton;
